import React, { useState, useEffect, useMemo, useCallback } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, where, documentId } from "firebase/firestore";
import CampaignDetail from "./CampaignDetail";
import { getCampaignCreativeIds } from "../utils/campaignCreativeIds";
import { FaFilter, FaSearch, FaSpinner, FaSort, FaSortUp, FaSortDown, FaSync } from 'react-icons/fa';
import dayjs from 'dayjs';

const PULSEFLOW_API = 'https://pulseflow.vuebillboards.com';
const MOBILE_MQ = '(max-width: 768px)';

/** Firestore `in` / `array-contains-any` disjunction limit */
const FIRESTORE_IN_MAX = 30;

const useIsMobile = () => {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => setMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return mobile;
};

const chunkIds = (ids, size) => {
  const out = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
};

const convertToDate = (timestamp) => {
  if (!timestamp) return null;
  if (typeof timestamp.toDate === "function") return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === "string") return new Date(timestamp);
  if (typeof timestamp.seconds === "number") return new Date(timestamp.seconds * 1000);
  return null;
};

const cleanAdvertiserDomain = (domain) => {
  if (!domain) return '';
  let cleaned = String(domain).trim();
  cleaned = cleaned.replace(/^https?:\/\//i, '');
  cleaned = cleaned.replace(/^www\./i, '');
  cleaned = cleaned.split('/')[0];
  cleaned = cleaned.split(':')[0];
  return cleaned;
};

const getCampaignAdvertiserDomain = (campaign, user) => {
  const candidates = [
    campaign?.advertiser_domain,
    campaign?.advertiserDomain,
    campaign?.clientDomain,
    campaign?.client_domain,
    campaign?.clientWebsite,
    campaign?.clientwebsite,
    campaign?.domain,
    campaign?.website,
    user?.website,
  ];
  for (const raw of candidates) {
    const cleaned = cleanAdvertiserDomain(raw);
    if (cleaned) return cleaned;
  }
  return '';
};

/** True when a per-publisher status means POST/sync with DSP (not local upload/pending). */
const publisherStatusIndicatesSubmittedToPerion = (raw) => {
  const v = String(raw || "").toLowerCase().trim();
  if (!v) return false;
  if (v === "pending" || v === "uploaded" || v === "draft") return false;
  return v === "submitted" || v === "approved" || v === "rejected" || v === "partial";
};

/** Moderation states where dspCreativeId* may exist locally before a successful Perion POST. */
const isPreSubmitModerationStatus = (moderationStatus) => {
  const ms = String(moderationStatus || "").toLowerCase().trim();
  return ms === "uploaded" || ms === "pending" || ms === "draft";
};

/** True if this creative is already in the Perion / DSP pipeline (do not show Vue pre-approval). */
const isCreativeSubmittedToPerion = (d) => {
  if (!d || typeof d !== "object") return false;
  if (d.dspSubmittedAt) return true;
  const ms = String(d.moderationStatus || "").toLowerCase().trim();
  if (ms === "submitted") return true;
  if (isPreSubmitModerationStatus(d.moderationStatus)) {
    if (d.statuses && typeof d.statuses === "object") {
      return Object.values(d.statuses).some(publisherStatusIndicatesSubmittedToPerion);
    }
    return false;
  }
  const single = String(d.dspCreativeId || d.dspCreativeID || d.dspId || "").trim();
  if (single) return true;
  if (Array.isArray(d.dspCreativeIds) && d.dspCreativeIds.some((x) => String(x || "").trim())) return true;
  const map = d.dspCreativeIdMap;
  if (map && typeof map === "object" && Object.values(map).some((x) => String(x || "").trim())) return true;
  if (d.statuses && typeof d.statuses === "object") {
    if (Object.values(d.statuses).some(publisherStatusIndicatesSubmittedToPerion)) return true;
  }
  return false;
};

const isCreativePendingPerionSubmission = (creativeData, campaign) => {
  if (!creativeData || !campaign) return false;
  if (isCreativeSubmittedToPerion(creativeData)) return false;
  const isPendingApproval =
    campaign.status === "Pending Vue Approval" ||
    campaign.status === "Awaiting Approval" ||
    campaign.status === "Draft";
  if (isPendingApproval) {
    return (
      !creativeData.approvedByAdmin ||
      isPreSubmitModerationStatus(creativeData.moderationStatus)
    );
  }
  const mod = String(creativeData.moderationStatus || "").toLowerCase();
  return (
    creativeData.nondsp ||
    mod === "pending" ||
    mod === "uploaded" ||
    mod === "flagged" ||
    creativeData.status === "flagged"
  );
};

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [statuses] = useState(["All", "Active (DSP)", "Paused (DSP)", "Pending Approval", "Draft", "Rejected", "Ended", "Error"]);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'startDate', direction: 'desc' });
  const [dspStatuses, setDspStatuses] = useState({});
  const [fetchingDspStatus, setFetchingDspStatus] = useState(false);
  const [pendingCreativesByCampaign, setPendingCreativesByCampaign] = useState({});
  const [loadingCreativeQueue, setLoadingCreativeQueue] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchUserInfoMap = async (list) => {
    const uniqueIds = [...new Set(list.map((c) => c.userUid).filter(Boolean))];
    if (!uniqueIds.length) return {};

    const batches = chunkIds(uniqueIds, FIRESTORE_IN_MAX);
    const snapshots = await Promise.all(
      batches.map((batch) =>
        getDocs(query(collection(db, "users"), where(documentId(), "in", batch)))
      )
    );

    const uMap = {};
    snapshots.forEach((snap) => {
      snap.forEach((userDoc) => {
        uMap[userDoc.id] = userDoc.data();
      });
    });
    return uMap;
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'campaigns'));
      const list = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter((c) => !c.isWarmup && !c.iswarmup && !String(c.id || '').startsWith('warmup-'));
      setCampaigns(list);

      const uMap = await fetchUserInfoMap(list);
      setUserInfo(uMap);

      // Fetch DSP status for all activated campaigns
      fetchDspStatuses(list);
      fetchPendingCreativeCounts(list);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCreativeCounts = async (campaignList) => {
    if (!Array.isArray(campaignList) || campaignList.length === 0) {
      setPendingCreativesByCampaign({});
      return;
    }

    setLoadingCreativeQueue(true);
    try {
      const allCreativeIds = Array.from(
        new Set(campaignList.flatMap((campaign) => getCampaignCreativeIds(campaign)))
      );

      if (!allCreativeIds.length) {
        setPendingCreativesByCampaign({});
        return;
      }

      const creativeBatches = chunkIds(allCreativeIds, FIRESTORE_IN_MAX);
      const creativeSnapshots = await Promise.all(
        creativeBatches.map((batch) =>
          getDocs(query(collection(db, "creatives"), where(documentId(), "in", batch)))
        )
      );

      const creativesById = {};
      creativeSnapshots.forEach((snap) => {
        snap.forEach((creativeDoc) => {
          creativesById[creativeDoc.id] = creativeDoc.data();
        });
      });

      const counts = {};
      campaignList.forEach((campaign) => {
        const creativeIds = getCampaignCreativeIds(campaign);
        const pendingCount = creativeIds.reduce((acc, creativeId) => {
          const creativeData = creativesById[creativeId];
          return acc + (isCreativePendingPerionSubmission(creativeData, campaign) ? 1 : 0);
        }, 0);
        counts[campaign.id] = pendingCount;
      });

      setPendingCreativesByCampaign(counts);
    } catch (error) {
      console.error("Error fetching pending creative counts:", error);
    } finally {
      setLoadingCreativeQueue(false);
    }
  };

  const fetchDspStatuses = async (campaignList) => {
    setFetchingDspStatus(true);
    const statusMap = {};
    
    // Filter campaigns that are DSP campaigns (have isActivated or dspInstanceId)
    const dspCampaigns = campaignList.filter(c => c.isActivated || c.dspInstanceId);
    
    // Fetch status for each DSP campaign
    const statusPromises = dspCampaigns.map(async (campaign) => {
      try {
        const response = await fetch(`${PULSEFLOW_API}/api/pacing/check/${campaign.id}`);
        const data = await response.json();
        
        if (data.success && data.data) {
          // Determine status from pacing data
          const isActive = data.data.plan?.status === 'active' || campaign.isActivated;
          statusMap[campaign.id] = {
            dspStatus: isActive ? 'Active (DSP)' : 'Paused (DSP)',
            health: data.data.plan?.health?.healthToday,
            spentToday: data.data.plan?.budgets?.spentToday,
            dailyBudget: data.data.plan?.budgets?.dailyBudgetLimit,
            lastChecked: new Date()
          };
        } else {
          // No DSP data available
          statusMap[campaign.id] = {
            dspStatus: campaign.isActivated ? 'Active (DSP)' : 'Paused (DSP)',
            lastChecked: new Date()
          };
        }
      } catch (error) {
        console.error(`Error fetching DSP status for campaign ${campaign.id}:`, error);
        statusMap[campaign.id] = {
          dspStatus: 'Error',
          error: error.message,
          lastChecked: new Date()
        };
      }
    });
    
    await Promise.all(statusPromises);
    setDspStatuses(statusMap);
    setFetchingDspStatus(false);
  };

  const refreshDspStatuses = () => {
    fetchDspStatuses(campaigns);
  };

  const isCampaignActive = (c) => {
    if (!c.startDate || !c.endDate) return false;
    const now = new Date();
    const s = convertToDate(c.startDate);
    const e = convertToDate(c.endDate);
    if (!s || !e) return false;
    return now >= s && now <= e;
  };

  const getCampaignStatus = useCallback(
    (c) => {
      const endDate = convertToDate(c.endDate);
      if (endDate && endDate < new Date()) return "Ended";

      if (dspStatuses[c.id]?.dspStatus) {
        return dspStatuses[c.id].dspStatus;
      }

      if (c.isActivated || c.dspInstanceId) {
        return fetchingDspStatus ? "Loading..." : c.isActivated ? "Active (DSP)" : "Paused (DSP)";
      }

      if (c.status === "Pending Vue Approval") return "Pending Approval";
      if (c.status === "Draft") return "Draft";
      if (c.status === "Rejected") return "Rejected";

      return c.status || "No Status";
    },
    [dspStatuses, fetchingDspStatus]
  );

  const dspStatusCounts = useMemo(() => {
    let activeDsp = 0;
    let pausedDsp = 0;
    for (const c of campaigns) {
      const s = getCampaignStatus(c);
      if (s === "Active (DSP)") activeDsp += 1;
      else if (s === "Paused (DSP)") pausedDsp += 1;
    }
    return { activeDsp, pausedDsp };
  }, [campaigns, getCampaignStatus]);

  const getStartDateValue = (c) => {
    if (!c?.startDate) return 0;
    if (typeof c.startDate.toDate === 'function') return c.startDate.toDate().getTime();
    if (typeof c.startDate.seconds === 'number') return c.startDate.seconds * 1000;
    const parsed = new Date(c.startDate).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const campaignMatchesSearch = (campaign, user, query) => {
    const raw = String(query || '').trim().toLowerCase();
    if (!raw) return true;
    const cleanedQuery = (cleanAdvertiserDomain(raw) || raw).toLowerCase();
    const haystacks = [
      getCampaignAdvertiserDomain(campaign, user),
      campaign?.name,
      campaign?.companyName,
      campaign?.email,
      campaign?.id,
      user?.name,
      user?.email,
      user?.companyName,
      user?.website,
    ].map((v) => String(v || '').toLowerCase());
    return haystacks.some((h) => h.includes(raw) || (cleanedQuery && h.includes(cleanedQuery)));
  };

  useEffect(() => {
    let filtered = campaigns;
    if (statusFilter !== "All") {
      filtered = filtered.filter(c => {
        const status = getCampaignStatus(c);
        return status === statusFilter;
      });
    }
    if (searchQuery.trim()) {
      filtered = filtered.filter((c) => campaignMatchesSearch(c, userInfo[c.userUid], searchQuery));
    }
    const sorted = [...filtered].sort((a, b) => {
      if (sortConfig.key === 'advertiserDomain') {
        const av = getCampaignAdvertiserDomain(a, userInfo[a.userUid]).toLowerCase();
        const bv = getCampaignAdvertiserDomain(b, userInfo[b.userUid]).toLowerCase();
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        const cmp = av.localeCompare(bv);
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      }
      const av = getStartDateValue(a);
      const bv = getStartDateValue(b);
      return sortConfig.direction === 'asc' ? av - bv : bv - av;
    });
    setFilteredCampaigns(sorted);
  }, [campaigns, statusFilter, searchQuery, sortConfig, getCampaignStatus, userInfo]);

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const formatDate = (ts) => ts ? dayjs(ts.seconds * 1000).format('MMM D, YYYY') : 'N/A';
  const getStatusColor = (s) => {
    if (s === 'Active (DSP)' || s === 'Active') return { bg: '#dcfce7', text: '#166534' };
    if (s === 'Paused (DSP)') return { bg: '#fff4ce', text: '#92400e' };
    if (s === 'Rejected') return { bg: '#fee2e2', text: '#991b1b' };
    if (s === 'Ended') return { bg: '#e5e7eb', text: '#4b5563' };
    if (s === 'Error') return { bg: '#fee2e2', text: '#991b1b' };
    if (s === 'Pending Approval' || s === 'Draft') return { bg: '#dbeafe', text: '#1e40af' };
    if (s === 'Loading...') return { bg: '#f3f4f6', text: '#6b7280' };
    return { bg: '#f3f2f1', text: '#64748b' };
  };

  const getCampaignRowView = (c) => {
    const s = getCampaignStatus(c);
    const colors = getStatusColor(s);
    const dspInfo = dspStatuses[c.id];
    const health = dspInfo?.health;
    const healthColor = health ? (health > 80 ? '#16a34a' : health > 50 ? '#ea580c' : '#dc2626') : '#6b7280';
    return {
      s,
      colors,
      dspInfo,
      health,
      healthColor,
      pendingQueueCount: pendingCreativesByCampaign[c.id] || 0,
      advertiserDomain: getCampaignAdvertiserDomain(c, userInfo[c.userUid]),
    };
  };

  const renderQueueStatus = (pendingQueueCount) => {
    if (loadingCreativeQueue) {
      return (
        <span style={{ fontSize: 11, color: '#9ca3af' }}>
          <FaSpinner className="fa-spin" style={{ marginRight: 6 }} />
          Checking...
        </span>
      );
    }
    if (pendingQueueCount > 0) {
      return (
        <span style={{ padding: '2px 8px', borderRadius: 2, fontSize: 11, fontWeight: 700, background: '#fff4ce', color: '#92400e' }}>
          {pendingQueueCount} waiting
        </span>
      );
    }
    return <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>Clear</span>;
  };

  const renderHealth = (health, healthColor) => {
    if (health === undefined) {
      return <span style={{ fontSize: 11, color: '#9ca3af' }}>—</span>;
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 60, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(health, 100)}%`, height: '100%', background: healthColor, transition: 'width 0.3s ease' }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: healthColor }}>
          {health.toFixed(0)}%
        </span>
      </div>
    );
  };

  const emptyMessage = searchQuery.trim()
    ? `No campaigns match “${searchQuery.trim()}”.`
    : 'No campaigns found.';

  return (
    <>
      <div className="f-page-enter mx-auto max-w-[1920px] px-4 pb-8 md:px-6">
        <div className="f-header rounded-xl border border-slate-200/80 shadow-sm">
          <div>
            <h1 className="f-title">Campaign Management</h1>
            <p className="f-subtitle mb-0">Manage and moderate all advertising campaigns</p>
          </div>
          <div className={`flex gap-2 ${isMobile ? 'w-full flex-col' : 'flex-wrap items-center'}`}>
            <div className={`relative ${isMobile ? 'w-full' : 'w-[260px]'}`}>
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-xs text-slate-500" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isMobile ? 'Search domain or name...' : 'Search domain, name, or user...'}
                className="f-input !w-full !pl-9"
              />
            </div>
            <div className={`relative ${isMobile ? 'w-full' : 'w-[200px]'}`}>
              <FaFilter className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-xs text-slate-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="f-input !w-full !pl-9"
              >
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <button
              type="button"
              onClick={refreshDspStatuses}
              disabled={fetchingDspStatus}
              className={`f-button f-button-secondary inline-flex items-center justify-center gap-2 ${isMobile ? 'w-full' : ''}`}
              title="Refresh DSP Status"
            >
              <FaSync className={fetchingDspStatus ? 'fa-spin' : ''} />
              {isMobile ? 'Refresh' : 'Refresh DSP Status'}
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className={isMobile ? 'mb-4 grid grid-cols-3 gap-2' : 'f-grid'}>
            <div className={`f-card f-stat-card ${isMobile ? '!mb-0 !p-3' : ''}`}>
              <span className="f-stat-label">{isMobile ? 'Total' : 'Total Campaigns'}</span>
              <span className={`f-stat-value ${isMobile ? '!my-0 !text-xl' : ''}`}>{campaigns.length}</span>
            </div>
            <div className={`f-card f-stat-card ${isMobile ? '!mb-0 !p-3' : ''}`}>
              <span className="f-stat-label">{isMobile ? 'Active' : 'Active (DSP)'}</span>
              <span className={`f-stat-value text-emerald-600 ${isMobile ? '!my-0 !text-xl' : ''}`}>
                {dspStatusCounts.activeDsp}
              </span>
            </div>
            <div className={`f-card f-stat-card ${isMobile ? '!mb-0 !p-3' : ''}`}>
              <span className="f-stat-label">{isMobile ? 'Paused' : 'Paused (DSP)'}</span>
              <span className={`f-stat-value text-orange-600 ${isMobile ? '!my-0 !text-xl' : ''}`}>
                {dspStatusCounts.pausedDsp}
              </span>
            </div>
          </div>

          {isMobile ? (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleSort('advertiserDomain')}
                  className={`f-button f-button-secondary !h-9 !min-h-9 gap-1.5 !px-3 text-xs ${sortConfig.key === 'advertiserDomain' ? '!border-primary !text-slate-900' : ''}`}
                >
                  Domain {getSortIcon('advertiserDomain')}
                </button>
                <button
                  type="button"
                  onClick={() => handleSort('startDate')}
                  className={`f-button f-button-secondary !h-9 !min-h-9 gap-1.5 !px-3 text-xs ${sortConfig.key === 'startDate' ? '!border-primary !text-slate-900' : ''}`}
                >
                  Date {getSortIcon('startDate')}
                </button>
              </div>
              {loading ? (
                <div className="f-card !mb-0 py-10 text-center text-sm text-slate-500">
                  <FaSpinner className="fa-spin" /> Loading...
                </div>
              ) : filteredCampaigns.length === 0 ? (
                <div className="f-card !mb-0 py-10 text-center text-sm text-slate-500">
                  {emptyMessage}
                </div>
              ) : filteredCampaigns.map((c) => {
                const { s, colors, dspInfo, health, healthColor, pendingQueueCount, advertiserDomain } = getCampaignRowView(c);
                const selected = selectedCampaign?.id === c.id;
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setSelectedCampaign(c)}
                    className="f-card !mb-0 w-full touch-manipulation p-4 text-left"
                    style={{
                      borderColor: selected ? '#2563eb' : undefined,
                      boxShadow: selected ? '0 0 0 2px rgba(37,99,235,0.12)' : undefined,
                      background: selected ? '#f8fafc' : undefined,
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="break-words text-[15px] font-semibold leading-snug text-slate-900">{c.name}</div>
                        <div className="mt-1 break-all font-mono text-xs text-slate-600">
                          {advertiserDomain || 'No domain'}
                        </div>
                      </div>
                      <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.text, flexShrink: 0 }}>
                        {s}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      <span>{userInfo[c.userUid]?.name || '—'}</span>
                      <span>{formatDate(c.startDate)}</span>
                    </div>
                    {dspInfo?.spentToday !== undefined && (
                      <div className="mt-1 text-[11px] text-slate-500">
                        ${dspInfo.spentToday?.toFixed(2) || '0.00'} / ${dspInfo.dailyBudget?.toFixed(2) || '0.00'} today
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between gap-3">
                      {renderQueueStatus(pendingQueueCount)}
                      {renderHealth(health, healthColor)}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
          <div className="f-card overflow-x-auto p-0">
            <table className="f-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('startDate')} style={{ cursor: 'pointer' }}>Start Date {getSortIcon('startDate')}</th>
                  <th>Campaign Name</th>
                  <th onClick={() => handleSort('advertiserDomain')} style={{ cursor: 'pointer' }}>Advertiser Domain {getSortIcon('advertiserDomain')}</th>
                  <th>User</th>
                  <th>Status</th>
                  <th>Creative Queue</th>
                  <th>DSP Health</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40 }}><FaSpinner className="fa-spin" /> Loading...</td></tr>
                ) : filteredCampaigns.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                      {emptyMessage}
                    </td>
                  </tr>
                ) : filteredCampaigns.map((c) => {
                  const { s, colors, dspInfo, health, healthColor, pendingQueueCount, advertiserDomain } = getCampaignRowView(c);
                  
                  return (
                    <tr key={c.id} onClick={() => setSelectedCampaign(c)} style={{ cursor: 'pointer', background: selectedCampaign?.id === c.id ? '#f1f5f9' : 'none' }}>
                      <td>{formatDate(c.startDate)}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        {dspInfo?.spentToday !== undefined && (
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            ${dspInfo.spentToday?.toFixed(2) || '0.00'} / ${dspInfo.dailyBudget?.toFixed(2) || '0.00'} today
                          </div>
                        )}
                      </td>
                      <td>
                        {advertiserDomain ? (
                          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 12 }}>
                            {advertiserDomain}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>—</span>
                        )}
                      </td>
                      <td>{userInfo[c.userUid]?.name || '—'}</td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: 2, fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.text }}>
                          {s}
                        </span>
                      </td>
                      <td>
                        {renderQueueStatus(pendingQueueCount)}
                      </td>
                      <td>
                        {renderHealth(health, healthColor)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </div>

      {selectedCampaign && (
        <CampaignDetail campaign={selectedCampaign} onClose={() => setSelectedCampaign(null)} />
      )}
    </>
  );
};

export default Campaigns;
